import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "submitted", label: "Submitted" },
  { value: "partially_paid", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "denied", label: "Denied" },
];

const SORT_OPTIONS = [
  { value: "oldest", label: "Oldest first" },
  { value: "newest", label: "Newest first" },
  { value: "highest", label: "Highest amount" },
  { value: "lowest", label: "Lowest amount" },
];

const STATUS_STYLES = {
  open: "bg-sky-50 text-sky-800 border-sky-200",
  submitted: "bg-amber-50 text-amber-800 border-amber-200",
  under_review: "bg-indigo-50 text-indigo-800 border-indigo-200",
  partially_paid: "bg-violet-50 text-violet-800 border-violet-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  denied: "bg-rose-50 text-rose-800 border-rose-200",
};

const ACTIONABLE_STATUSES = new Set(["open", "submitted", "partially_paid"]);

const HMO_TUTORIAL_CLAIMS = [
  demoClaim({
    id: "hmo-demo-open",
    patient_name: "Juan Dela Cruz",
    payer_name: "Maxicare",
    loa_number: "LOA-HMO-1001",
    member_id: "MX-77881",
    covered_centavos: 180000,
    status: "open",
    days_pending: 1,
  }),
  demoClaim({
    id: "hmo-demo-submitted",
    patient_name: "Ana Santos",
    payer_name: "Intellicare",
    loa_number: "LOA-HMO-1002",
    member_id: "IC-44120",
    covered_centavos: 250000,
    status: "submitted",
    days_pending: 11,
  }),
  demoClaim({
    id: "hmo-demo-partial",
    patient_name: "Ben Reyes",
    payer_name: "Medicard",
    loa_number: "LOA-HMO-1003",
    member_id: "MC-90214",
    covered_centavos: 320000,
    paid_centavos: 150000,
    ar_outstanding_centavos: 170000,
    status: "partially_paid",
    days_pending: 24,
  }),
];

export default function InsuranceClaims() {
  const { activeClinic } = useAuth();
  const { activeBranchId, loading: branchLoading } = useBranch();
  const currency = activeClinic?.currency || "PHP";
  const isHmoTutorialDemo = isHmoClaimsTutorialDemo();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(() => emptySummary());
  const [statusCounts, setStatusCounts] = useState(() => emptyStatusCounts());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("oldest");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyClaimId, setBusyClaimId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [settlement, setSettlement] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (isHmoTutorialDemo) {
      const demoRows = filterAndSortDemoClaims(HMO_TUTORIAL_CLAIMS, { search, sort, status });
      setRows(demoRows);
      setTotalCount(HMO_TUTORIAL_CLAIMS.length);
      setSummary(summaryFromRows(HMO_TUTORIAL_CLAIMS));
      setStatusCounts(statusCountsFromRows(HMO_TUTORIAL_CLAIMS));
      setLoading(false);
      return;
    }
    if (!activeBranchId) {
      setRows([]);
      setTotalCount(0);
      setSummary(emptySummary());
      setStatusCounts(emptyStatusCounts());
      setLoading(Boolean(branchLoading));
      setError(branchLoading ? null : "No active branch selected.");
      return;
    }
    const { data, error: e } = await supabase.rpc("list_insurance_claims", {
      p_input: {
        p_status: status === "all" ? "" : status,
        p_search: search.trim(),
        p_sort: sort,
        p_page: 1,
        p_page_size: 100,
        p_branch_id: activeBranchId,
      },
    });
    setLoading(false);
    if (e) {
      setRows([]);
      setTotalCount(0);
      setSummary(emptySummary());
      setStatusCounts(emptyStatusCounts());
      setError(e.message ?? "Could not load HMO claims.");
      return;
    }
    setRows(Array.isArray(data?.rows) ? data.rows : []);
    setTotalCount(Number(data?.total_count || 0));
    setSummary(normalizeSummary(data?.summary));
    setStatusCounts(normalizeStatusCounts(data?.status_counts));
  }, [activeBranchId, branchLoading, isHmoTutorialDemo, search, sort, status]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = rows;

  const selectedClaims = useMemo(
    () => rows.filter((claim) => selectedIds.has(claim.id)),
    [rows, selectedIds],
  );

  async function settleClaim({ claim, action, amountCentavos = null, note = null }) {
    if (isHmoTutorialDemo) {
      setBusyClaimId(claim.id);
      const nextRows = rows.map((row) => (
        row.id === claim.id ? applyDemoClaimAction(row, action, amountCentavos) : row
      ));
      setRows(nextRows);
      setSummary(summaryFromRows(nextRows));
      setStatusCounts(statusCountsFromRows(nextRows));
      setBusyClaimId(null);
      toast.success(claimActionSuccess(action));
      setSettlement(null);
      setSelectedIds(new Set());
      return true;
    }
    setBusyClaimId(claim.id);
    const { error: e } = await supabase.rpc("settle_insurance_claim", {
      p_claim_id: claim.id,
      p_action: action,
      p_amount_centavos: amountCentavos,
      p_note: note,
    });
    setBusyClaimId(null);
    if (e) {
      toast.error(e.message ?? "Claim update failed.");
      return false;
    }
    toast.success(claimActionSuccess(action));
    setSettlement(null);
    setSelectedIds(new Set());
    await load();
    return true;
  }

  async function bulkSubmit() {
    const openClaims = selectedClaims.filter((claim) => (claim.status || "open") === "open");
    if (openClaims.length === 0) {
      toast.error("Select at least one open claim.");
      return;
    }
    if (isHmoTutorialDemo) {
      const selectedOpenIds = new Set(openClaims.map((claim) => claim.id));
      const nextRows = rows.map((row) => (
        selectedOpenIds.has(row.id) ? applyDemoClaimAction(row, "submitted") : row
      ));
      setRows(nextRows);
      setSummary(summaryFromRows(nextRows));
      setStatusCounts(statusCountsFromRows(nextRows));
      setSelectedIds(new Set());
      toast.success(`${openClaims.length} claim${openClaims.length === 1 ? "" : "s"} submitted.`);
      return;
    }
    setBulkBusy(true);
    const succeeded = [];
    const failed = [];
    for (const claim of openClaims) {
      const { error: e } = await supabase.rpc("settle_insurance_claim", {
        p_claim_id: claim.id,
        p_action: "submitted",
        p_amount_centavos: null,
        p_note: null,
      });
      if (e) failed.push(claim.patient_name || claim.loa_number || claim.id);
      else succeeded.push(claim.patient_name || claim.loa_number || claim.id);
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    if (failed.length > 0) {
      toast.error(`${succeeded.length} submitted, ${failed.length} failed.`);
    } else {
      toast.success(`${succeeded.length} claim${succeeded.length === 1 ? "" : "s"} submitted.`);
    }
    await load();
  }

  function toggleSelected(claimId, checked) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(claimId);
      else next.delete(claimId);
      return next;
    });
  }

  return (
    <div className="space-y-5" data-testid="insurance-claims-page" data-tutorial-id="insurance-claims-page">
      <header className="flex flex-col gap-3 border-b border-slate-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-teal-700">
            <ShieldCheck className="h-4 w-4" />
            HMO Claims
          </div>
          <h1 className="mt-1 text-2xl font-bold text-slate-950">HMO Claims</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track HMO receivables, submit claims, and post reimbursements through the settlement RPC.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="claims-refresh"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </header>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" aria-label="HMO claim summary" data-tutorial-id="insurance-claims-summary">
        <SummaryStat label="Open" value={summary.open} detail="awaiting submission" />
        <SummaryStat label="Submitted" value={summary.submitted} detail="with provider" />
        <SummaryStat label="Uncollected" value={formatMoney(summary.uncollected, currency)} detail="total pending AR" />
        <SummaryStat
          label="Collected this month"
          value={formatMoney(summary.collectedThisMonth, currency)}
          detail={`${summary.claimsPaidThisMonth} claim${summary.claimsPaidThisMonth === 1 ? "" : "s"} paid`}
        />
      </section>

      <section className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onInput={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search patient, LOA, or HMO provider"
            data-testid="claims-search"
            className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
          />
        </div>
        <select
          value={sort}
          onChange={(event) => setSort(event.target.value)}
          data-testid="claims-sort"
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </section>

      <section className="flex flex-wrap gap-2" aria-label="Claim status filters" data-tutorial-id="insurance-claims-status-filters">
        {STATUS_FILTERS.map((option) => {
          const active = status === option.value;
          return (
            <button
              type="button"
              key={option.value}
              onClick={() => {
                setStatus(option.value);
                setSelectedIds(new Set());
              }}
              className={`rounded-md border px-3 py-2 text-sm font-bold transition ${
                active
                  ? "border-teal-500 bg-teal-50 text-teal-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {option.label} {statusCounts[option.value] || 0}
            </button>
          );
        })}
      </section>

      {selectedIds.size > 0 ? (
        <section className="flex flex-col gap-3 rounded-md border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-950 md:flex-row md:items-center md:justify-between">
          <div className="font-semibold">{selectedIds.size} selected</div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="rounded-md px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={bulkSubmit}
              disabled={bulkBusy}
              data-testid="bulk-submit-claims"
              data-tutorial-id="insurance-claims-bulk-submit"
              className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
              Mark open as submitted
            </button>
          </div>
        </section>
      ) : null}

      {error ? (
        <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading claims
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-700">No claims found.</p>
            <p className="mt-1 text-xs text-slate-500">Change the search or status filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left text-sm">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">LOA number</th>
                  <th className="px-4 py-3 text-right">HMO covers</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Pending</th>
                  <th className="w-14 px-4 py-3"><span className="sr-only">Expand</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleRows.map((claim) => (
                  <ClaimRow
                    key={claim.id}
                    claim={claim}
                    currency={currency}
                    selected={selectedIds.has(claim.id)}
                    expanded={expandedId === claim.id}
                    busy={busyClaimId === claim.id}
                    onSelect={(checked) => toggleSelected(claim.id, checked)}
                    onExpand={() => setExpandedId((current) => (current === claim.id ? null : claim.id))}
                    onSubmit={() => settleClaim({ claim, action: "submitted" })}
                    onPaid={() => setSettlement({ claim })}
                    onDeny={() => settleClaim({ claim, action: "denied" })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-slate-100 px-4 py-3 text-xs font-semibold text-slate-500">
          {visibleRows.length} shown, {totalCount} total
        </div>
      </section>

      {settlement ? (
        <SettlementDialog
          claim={settlement.claim}
          currency={currency}
          busy={busyClaimId === settlement.claim.id}
          onClose={() => setSettlement(null)}
          onConfirm={(payload) => settleClaim(payload)}
        />
      ) : null}
    </div>
  );
}

function SummaryStat({ label, value, detail }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-4">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold tabular-nums text-slate-950">{value}</div>
      <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
    </div>
  );
}

function ClaimRow({
  claim,
  currency,
  selected,
  expanded,
  busy,
  onSelect,
  onExpand,
  onSubmit,
  onPaid,
  onDeny,
}) {
  const claimStatus = claim.status || "open";
  const covered = Number(claim.covered_centavos || 0);
  const paid = Number(claim.paid_centavos || 0);
  const outstanding = getClaimOutstandingCentavos(claim);
  const pendingDays = Number(claim.days_pending ?? claim.aging_days ?? 0);
  const pendingClass = pendingDays > 14 && (claimStatus === "open" || claimStatus === "submitted" || claimStatus === "partially_paid")
    ? "text-rose-600"
    : "text-slate-700";
  const canAct = ACTIONABLE_STATUSES.has(claimStatus);

  return (
    <Fragment>
      <tr data-testid={`claim-row-${claim.id}`} data-tutorial-id={`insurance-claim-row-${claim.id}`} className="align-top hover:bg-slate-50">
        <td className="px-4 py-4">
          <input
            type="checkbox"
            checked={selected}
            onChange={(event) => onSelect(event.target.checked)}
            onClick={(event) => event.stopPropagation()}
            data-testid={`claim-select-${claim.id}`}
            data-tutorial-id={`insurance-claim-select-${claim.id}`}
            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
          />
        </td>
        <td className="px-4 py-4">
          <div className="font-semibold text-slate-950">{claim.patient_name || "Unknown patient"}</div>
          <div className="text-xs font-semibold text-slate-500">{formatDate(claim.visit_date || claim.service_date)}</div>
        </td>
        <td className="px-4 py-4 font-semibold text-slate-700">{claim.payer_name || claim.payer_short_name || "Unknown HMO"}</td>
        <td className="px-4 py-4 font-mono text-xs text-slate-700">{claim.loa_number || "No LOA"}</td>
        <td className="px-4 py-4 text-right">
          <div className="font-bold tabular-nums text-slate-950">{formatMoney(covered, currency)}</div>
          {outstanding > 0 ? (
            <div className="text-xs font-bold tabular-nums text-rose-600">{formatMoney(outstanding, currency)} owed</div>
          ) : null}
        </td>
        <td className="px-4 py-4">
          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${STATUS_STYLES[claimStatus] || "border-slate-200 bg-slate-50 text-slate-700"}`}>
            {statusLabel(claimStatus)}
          </span>
        </td>
        <td className={`px-4 py-4 text-right font-bold tabular-nums ${pendingClass}`}>{pendingDays}d</td>
        <td className="px-4 py-4 text-right">
          <button
            type="button"
            onClick={onExpand}
            data-testid={`claim-expand-${claim.id}`}
            data-tutorial-id={`insurance-claim-expand-${claim.id}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${claim.patient_name || "claim"}`}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={8} className="bg-slate-50 px-4 py-4" data-tutorial-id={`insurance-claim-detail-${claim.id}`}>
            <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-start">
              <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Detail label="Member ID" value={claim.member_id || "Not recorded"} />
                <Detail label="LOA number" value={claim.loa_number || "Not recorded"} />
                <Detail label="Provider" value={claim.payer_name || claim.payer_short_name || "Unknown HMO"} />
                <Detail label="Visit date" value={formatDate(claim.visit_date || claim.service_date)} />
                <Detail label="HMO covered" value={formatMoney(covered, currency)} />
                <Detail label="Collected" value={formatMoney(paid, currency)} />
                <Detail label="Still owed" value={formatMoney(outstanding, currency)} tone={outstanding > 0 ? "danger" : "normal"} />
              </dl>
              {canAct ? (
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  {claimStatus === "open" ? (
                    <button
                      type="button"
                      onClick={onSubmit}
                      disabled={busy}
                      data-testid={`claim-submit-${claim.id}`}
                      data-tutorial-id={`insurance-claim-submit-${claim.id}`}
                      className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock3 className="h-4 w-4" />}
                      Mark submitted
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={onPaid}
                    disabled={busy}
                    data-testid={`claim-paid-${claim.id}`}
                    data-tutorial-id={`insurance-claim-paid-${claim.id}`}
                    className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Mark paid
                  </button>
                  <button
                    type="button"
                    onClick={onDeny}
                    disabled={busy}
                    data-testid={`claim-denied-${claim.id}`}
                    data-tutorial-id={`insurance-claim-denied-${claim.id}`}
                    className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Deny
                  </button>
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

function Detail({ label, value, tone = "normal" }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`mt-1 text-sm font-bold ${tone === "danger" ? "text-rose-600" : "text-slate-900"}`}>{value}</dd>
    </div>
  );
}

function SettlementDialog({ claim, currency, busy, onClose, onConfirm }) {
  const remaining = getClaimOutstandingCentavos(claim);
  const [amount, setAmount] = useState(centsToPeso(remaining));
  const amountCentavos = pesoToCents(amount);
  const disabled = busy || amountCentavos <= 0;
  const nextAction = amountCentavos >= remaining ? "paid" : "partially_paid";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-950">Mark claim paid</h2>
          <p className="mt-1 text-sm text-slate-500">
            {claim.patient_name} · {claim.payer_name} · {claim.loa_number || "No LOA"}
          </p>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-slate-500" htmlFor="claim-settle-amount">
              Amount received
            </label>
            <input
              id="claim-settle-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              onInput={(event) => setAmount(event.currentTarget.value)}
              data-testid="claim-settle-amount"
              data-tutorial-id="insurance-claim-settle-amount"
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            />
            <p className="mt-1 text-xs text-slate-500">
              Remaining expected: {formatMoney(remaining, currency)}. This will mark the claim {statusLabel(nextAction).toLowerCase()}.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({
              claim,
              action: nextAction,
              amountCentavos,
              note: null,
            })}
            disabled={disabled}
            data-testid="claim-settle-confirm"
            data-tutorial-id="insurance-claim-settle-confirm"
            className="inline-flex items-center gap-2 rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function isHmoClaimsTutorialDemo() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("tutorial") === "hmo-checkout-claims" && params.get("demo") === "1";
}

function demoClaim(overrides) {
  const covered = Number(overrides.covered_centavos || 0);
  const paid = Number(overrides.paid_centavos || 0);
  return {
    visit_date: "2026-05-29T09:30:00.000Z",
    service_date: "2026-05-29",
    ar_amount_centavos: covered,
    ar_outstanding_centavos: Math.max(0, covered - paid),
    paid_centavos: paid,
    ...overrides,
  };
}

function filterAndSortDemoClaims(sourceRows, { search, sort, status }) {
  const needle = String(search || "").trim().toLowerCase();
  const filtered = sourceRows.filter((claim) => {
    const claimStatus = claim.status || "open";
    if (status !== "all" && claimStatus !== status) return false;
    if (!needle) return true;
    return [
      claim.patient_name,
      claim.payer_name,
      claim.payer_short_name,
      claim.loa_number,
      claim.member_id,
    ].some((value) => String(value || "").toLowerCase().includes(needle));
  });
  return [...filtered].sort((a, b) => {
    if (sort === "newest") return String(b.service_date || "").localeCompare(String(a.service_date || ""));
    if (sort === "highest") return Number(b.covered_centavos || 0) - Number(a.covered_centavos || 0);
    if (sort === "lowest") return Number(a.covered_centavos || 0) - Number(b.covered_centavos || 0);
    return String(a.service_date || "").localeCompare(String(b.service_date || ""));
  });
}

function applyDemoClaimAction(claim, action, amountCentavos = null) {
  const covered = Number(claim.covered_centavos || 0);
  const paidSoFar = Number(claim.paid_centavos || 0);
  if (action === "submitted") {
    return { ...claim, status: "submitted" };
  }
  if (action === "denied") {
    return { ...claim, status: "denied" };
  }
  if (action === "paid" || action === "partially_paid") {
    const received = amountCentavos == null ? Math.max(0, covered - paidSoFar) : Number(amountCentavos || 0);
    const paid = Math.min(covered, paidSoFar + received);
    const outstanding = Math.max(0, covered - paid);
    return {
      ...claim,
      paid_centavos: paid,
      ar_outstanding_centavos: outstanding,
      status: outstanding > 0 ? "partially_paid" : "paid",
    };
  }
  return claim;
}

function summaryFromRows(rows) {
  return {
    open: rows.filter((claim) => (claim.status || "open") === "open").length,
    submitted: rows.filter((claim) => (claim.status || "open") === "submitted").length,
    uncollected: rows.reduce((sum, claim) => sum + getClaimOutstandingCentavos(claim), 0),
    collectedThisMonth: rows.reduce((sum, claim) => sum + Number(claim.paid_centavos || 0), 0),
    claimsPaidThisMonth: rows.filter((claim) => (claim.status || "open") === "paid").length,
  };
}

function statusCountsFromRows(rows) {
  const counts = emptyStatusCounts();
  counts.all = rows.length;
  for (const claim of rows) {
    const status = claim.status || "open";
    counts[status] = Number(counts[status] || 0) + 1;
  }
  return counts;
}

function claimActionSuccess(action) {
  if (action === "submitted") return "Claim marked submitted.";
  if (action === "paid") return "Claim marked paid.";
  if (action === "partially_paid") return "Claim marked partially paid.";
  if (action === "denied") return "Claim marked denied.";
  return "Claim updated.";
}

function statusLabel(status) {
  return String(status || "open")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function emptySummary() {
  return {
    open: 0,
    submitted: 0,
    uncollected: 0,
    collectedThisMonth: 0,
    claimsPaidThisMonth: 0,
  };
}

function normalizeSummary(raw) {
  return {
    open: Number(raw?.open_count || 0),
    submitted: Number(raw?.submitted_count || 0),
    uncollected: Number(raw?.uncollected_centavos || 0),
    collectedThisMonth: Number(raw?.collected_this_month_centavos || 0),
    claimsPaidThisMonth: Number(raw?.claims_paid_this_month || 0),
  };
}

function emptyStatusCounts() {
  return STATUS_FILTERS.reduce((acc, option) => {
    acc[option.value] = 0;
    return acc;
  }, {});
}

function normalizeStatusCounts(raw) {
  const counts = emptyStatusCounts();
  for (const option of STATUS_FILTERS) {
    counts[option.value] = Number(raw?.[option.value] || 0);
  }
  return counts;
}

function getClaimOutstandingCentavos(claim) {
  const arOutstanding = Number(claim?.ar_outstanding_centavos);
  if (Number.isFinite(arOutstanding)) return Math.max(0, arOutstanding);
  return Math.max(0, Number(claim?.covered_centavos || 0) - Number(claim?.paid_centavos || 0));
}

function formatDate(value) {
  if (!value) return "No visit date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatMoney(value, currency = "PHP") {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0) / 100);
}

function centsToPeso(value) {
  return (Number(value || 0) / 100).toFixed(2);
}

function pesoToCents(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}
